import React, { useState, useEffect } from "react";
import { HeaderComponent, ButtonComponent, Editor, SkeletonComponent } from '@comps/components';
import { Steps, Form, Select, Row, Col, Input, Typography } from 'antd';
import toast from "@comps/toast";
import { Icon } from '@iconify/react';
import { getErrorProps } from "@utils/helpers";
import { crowdingData, spacingData, overbiteData, openbiteData, classIIData, classIIIData, crossbiteData } from "./treatmentdata";
import { useParams, useNavigate } from 'react-router-dom';
import TeethIcon from "./TeethIcon"
import TreatmentStepRange from "./TreatmentStepRange";
import { firstTreatmentStepError } from "./treatmentSteps";
import "../treatmentPlan.css"
import { updateUserCasTreatmentPlanDataReq, getUserCaseTreatementReq } from '../requests';
import { makeRequest, parseJsonList } from '@utils/helpers';

const { Text } = Typography;

// ---------------------------------------------------------------------
// Small presentational helper (pure layout/styling — no business logic)
// ---------------------------------------------------------------------
const SectionCard = ({ icon, title, subtitle, children, style }) => (
  <section className="tp-card" style={ style }>
    <div className="tp-card-head">
      { icon &&
        <span className="tp-card-mark">
          <Icon icon={ icon } width={ 18 } />
        </span>
      }
      <h2 className="tp-card-title">{ title }</h2>
    </div>
    { subtitle && <p className="tp-card-sub">{ subtitle }</p> }
    { children }
  </section>
);

const inputHeight = { height: '40px' };


const pageConfig = {
  headers: {
    title: "Update Case Treatment",
    breadcrumb: [
      {
        name: "Update Case Treatment",
        path: "/cases"
      }
    ]
  }
}

const formName = "treatmentForm"

// Fields that must be filled in before leaving a step — required fields
// mounted on a step nobody has visited yet were only ever checked at
// final Save, so a step could be skipped over with them still empty.
const FIELDS_BY_STEP = {
  1: ['aligner_upper_jaw', 'aligner_lower_jaw', 'attachment_upper_jaw', 'attachment_lower_jaw'],
};

// The attachment template fields only ever mean 0 or 1 — block every
// other key rather than let it in and then show an error about it.
const restrictToBinaryDigit = (e) => {
  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
  if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
  if (e.key !== '0' && e.key !== '1') {
    e.preventDefault();
  }
};

const stepItems = [
  { title: 'Description', icon: 'tabler:report-medical' },
  { title: 'Amount of aligners', icon: 'tabler:layers-intersect' },
  { title: 'Orthodontist notes', icon: 'tabler:stethoscope' },
  { title: 'IPR and attachments', icon: 'tabler:tooth' },
  { title: 'Technician\'s notes', icon: 'tabler:notes' },
  { title: '3D Set up', icon: 'tabler:cube-3d-sphere' },
];

const UpdateTreatmentPlan = () => {
  const { id } = useParams();
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [errors, setErrors] = useState([])
  // Loading the plan and saving it are different waits with different
  // consequences, so they get separate flags — one shared flag put the
  // Save button on "Working…" while the plan was still being fetched.
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [orthodontistNote, setOrthodontistNote] = useState("")
  const [technicianNote, setTechnicianNote] = useState("")
  const [treamentSteps, setTreamentSteps] = useState([0])
  const [treamentStepsData, setTreamentStepsData] = useState([{ "from": '', "to": '' }])
  const [iprs, setIprs] = useState([])
  const [selectedAttachments, setSelectedAttachments] = useState([])
  const [currentTreatmentData, setCurrentTreatmentData] = useState(null)


  useEffect(() => {
    makeRequest(setLoading, getUserCaseTreatementReq, id, onSuccessGetData, onError);
  }, [])

  const onSuccessGetData = (data) => {
    // A plan id that no longer resolves comes back as a 200 with null
    // data, not as an error, and reading through it threw before the
    // page had rendered anything — a blank screen with no explanation.
    if (! data) {
      toast.error("This treatment plan could not be found");
      return;
    }
    setCurrentTreatmentData(data)
    setOrthodontistNote(data.orthodontist_notes ?? "")
    setTechnicianNote(data.technician_notes ?? "")
    if (data.iprsattachment) {
      // Read through parseJsonList rather than JSON.parse: these are
      // JSON in text columns, and a plan written before they existed
      // carries null, which parses to null and then threw on .map —
      // the page died on load with nothing on screen to say why.
      const steps = parseJsonList(data.iprsattachment.treatment_steps)
      setTreamentStepsData(steps.length ? steps : [{ from: '', to: '' }])
      setIprs(parseJsonList(data.iprsattachment.iprs))
      setSelectedAttachments(parseJsonList(data.iprsattachment.attachments))
      setTreamentSteps((steps.length ? steps : [0]).map((each, index) => index))
    }
  }

  /** One end of one step's aligner range. */
  const updateStepRange = (index, field, value) => {
    setTreamentStepsData((prev) => {
      const data = [...prev];
      data[index] = { ...data[index], [field]: value };
      return data;
    });
  };

  /*
   * The IPR step's From/To boxes are not Form.Items — they are held in
   * treamentStepsData, so antd's own validation never saw them and the
   * screen would happily save a range that was empty, negative,
   * fractional or in the billions. Leaving that step, and saving, both
   * go through here.
   */
  const blockedByStepRange = () => {
    const problem = firstTreatmentStepError(treamentStepsData);
    if (! problem) return false;
    setCurrentStep(3);
    toast.error("Check the treatment steps", problem);
    return true;
  };

  const onFinish = (data) => {
    // belt and braces: the request can only leave from the final step
    if (currentStep !== 5) return;
    if (blockedByStepRange()) return;
    let payload = {
      ...data,
      id: id,
      orthodontist_notes: orthodontistNote,
      technician_notes: technicianNote,
      treatment_steps: JSON.stringify(treamentStepsData),
      iprs: JSON.stringify(iprs),
      attachments: JSON.stringify(selectedAttachments)
    }

    makeRequest(setSaving, updateUserCasTreatmentPlanDataReq, payload, onSuccess, onError);
  }

  const onError = (error, res) => {
    // A request that never reached the API hands back an Error here, not
    // the app's envelope, so there is no `msg` on it to read.
    toast.error((res && res.msg) || "Something went wrong. Please try again.")
  }

  const onSuccess = (data) => {
    toast.success("Treatment Plan Added Successfully")
    navigate(`/cases/details/${currentTreatmentData.case_id}`)
  }

  return (<>
    <HeaderComponent headers={ pageConfig.headers } />
    <div className="tp-page">
      {/* Until the plan is in hand the page shows the shape of it rather
          than an empty stepper over nothing. */}
      { loading && <SkeletonComponent variant="sections" rows={ 2 } actions /> }

      <div className="tp-steps" style={ loading ? { display: 'none' } : undefined }>
        <Steps
          size="small"
          current={ currentStep }
          responsive={ false }
          labelPlacement="vertical"
          items={ stepItems.map(s => ({ title: s.title })) }
        />
        {/* the horizontal stepper is unreadable on a phone, so narrow
            screens get the position stated plainly instead */ }
        <p className="tp-steps-compact">
          Step { currentStep + 1 } of { stepItems.length }
          <strong>{ stepItems[currentStep] && stepItems[currentStep].title }</strong>
        </p>
      </div>
      { currentTreatmentData &&
        <Form
          form={ form }
          layout="vertical"
          name={ formName }
          onKeyDown={ (e) => {
            // Enter never submits on its own; on the last step, Enter in a
            // plain input does what the Save button does.
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
              e.preventDefault();
              if (currentStep === 5 && e.target.tagName === 'INPUT' && ! e.nativeEvent.isComposing && ! saving) {
                form.submit();
              }
            }
          } }
          onFinish={ onFinish }
          initialValues={ currentTreatmentData }
        >

          <div style={ { display: currentStep === 0 ? 'block' : 'none' } } className="step1">
            <SectionCard icon="tabler:report-medical" title="Description" subtitle="Select the type of malocclusion and treatment plan and the doctor will see a description with a video">
              <Row gutter={ 20 }>
                <Col xs={ 24 } md={ 12 }>
                  <Form.Item onChange={
                    () => setErrors([])
                  } name="crowding" rules={ rules.Crowding } label="Crowding :" className="da-mb-16"
                    { ...getErrorProps(errors['Crowding']) }
                  >
                    <Select style={ inputHeight } options={ crowdingData } placeholder="Select Crowding " />
                  </Form.Item>
                </Col>
                <Col xs={ 24 } md={ 12 }>
                  <Form.Item onChange={
                    () => setErrors([])
                  } name="spacing" rules={ rules.Spacing } label="Spacing :"
                    { ...getErrorProps(errors['Spacing']) }
                  >
                    <Select style={ inputHeight } options={ spacingData } placeholder="Select Spacing " />
                  </Form.Item>
                </Col>
                <Col xs={ 24 } md={ 12 }>
                  <Form.Item onChange={
                    () => setErrors([])
                  } name="overbite" rules={ rules.Overbite } label="Overbite :"
                    { ...getErrorProps(errors['Overbite']) }
                  >
                    <Select style={ inputHeight } options={ overbiteData } placeholder="Select Overbite " />
                  </Form.Item>
                </Col>
                <Col xs={ 24 } md={ 12 }>
                  <Form.Item onChange={
                    () => setErrors([])
                  } name="openbite" rules={ rules.Openbite } label="Openbite :"
                    { ...getErrorProps(errors['Openbite']) }
                  >
                    <Select style={ inputHeight } options={ openbiteData } placeholder="Select Openbite " />
                  </Form.Item>
                </Col>
                <Col xs={ 24 } md={ 12 }>
                  <Form.Item onChange={
                    () => setErrors([])
                  } name="class2" rules={ rules.classII } label="Class II :"
                    { ...getErrorProps(errors['Class II']) }
                  >
                    <Select style={ inputHeight } options={ classIIData } placeholder="Select Class II " />
                  </Form.Item>
                </Col>
                <Col xs={ 24 } md={ 12 }>
                  <Form.Item onChange={
                    () => setErrors([])
                  } name="class3" rules={ rules.ClassIII } label="Class III :"
                    { ...getErrorProps(errors['Class III']) }
                  >
                    <Select style={ inputHeight } options={ classIIIData } placeholder="Select Class III " />
                  </Form.Item>
                </Col>
                <Col xs={ 24 } md={ 12 }>
                  <Form.Item onChange={
                    () => setErrors([])
                  } name="crossbite" rules={ rules.Crossbite } label="Crossbite :"
                    { ...getErrorProps(errors['Crossbite']) }
                  >
                    <Select style={ inputHeight } options={ crossbiteData } placeholder="Select Crossbite " />
                  </Form.Item>
                </Col>
              </Row>
            </SectionCard>
          </div>

          <div style={ { display: currentStep === 1 ? 'block' : 'none' } } className="step2">
            <SectionCard icon="tabler:layers-intersect" title="Amount of aligners" subtitle="Add amount of aligners">
              <Row gutter={ 20 }>
                <Col xs={ 24 } md={ 12 }>
                  <Form.Item initialValue={ 0 } onChange={
                    () => setErrors([])
                  } name="aligner_upper_jaw" dependencies={ ['aligner_lower_jaw'] } rules={ rules.uperjaw } label="Aligner to the upper jaw :"
                    { ...getErrorProps(errors['uperjaw']) }
                  >
                    <Input style={ inputHeight } type="number" />
                  </Form.Item>
                </Col>
                <Col xs={ 24 } md={ 12 }>
                  <Form.Item initialValue={ 0 } onChange={
                    () => setErrors([])
                  } name="aligner_lower_jaw" dependencies={ ['aligner_upper_jaw'] } rules={ rules.lowerjaw } label="Aligner to the lower jaw :"
                    { ...getErrorProps(errors['lowerjaw']) }
                  >
                    <Input style={ inputHeight } type="number" />
                  </Form.Item>
                </Col>
                <Col xs={ 24 } md={ 12 }>
                  <Form.Item initialValue={ 0 } onChange={
                    () => setErrors([])
                  } name="attachment_upper_jaw" rules={ rules.attachmentUpperjaw } label="Templates for attachment to the upper jaw :"
                    { ...getErrorProps(errors['attachmentUpperjaw']) }
                  >
                    <Input style={ inputHeight } type="number" min={ 0 } max={ 1 } onKeyDown={ restrictToBinaryDigit } />
                  </Form.Item>
                </Col>
                <Col xs={ 24 } md={ 12 }>
                  <Form.Item initialValue={ 0 } onChange={
                    () => setErrors([])
                  } name="attachment_lower_jaw" rules={ rules.attachmentLowerjaw } label="Templates for attachment to the lower jaw :"
                    { ...getErrorProps(errors['attachmentLowerjaw']) }
                  >
                    <Input style={ inputHeight } type="number" min={ 0 } max={ 1 } onKeyDown={ restrictToBinaryDigit } />
                  </Form.Item>
                </Col>
              </Row>
            </SectionCard>
          </div>
          <div style={ { display: currentStep === 2 ? 'block' : 'none' } } className="step3">
            <SectionCard icon="tabler:stethoscope" title="Orthodontist notes" subtitle="Add notes and comments">
              <Editor style={ { height: '200px' } } onChange={ (e) => setOrthodontistNote(e) } inialData={ orthodontistNote } />
            </SectionCard>
          </div>

          <div style={ { display: currentStep === 3 ? 'block' : 'none' } } className="step3">
            <SectionCard icon="tabler:tooth" title="IPR and attachments" subtitle="Specify the places and stages of IPR implementation and fixation of attachments">
              { treamentSteps && treamentSteps.length > 0 && treamentSteps.map((each, index) =>
                <div key={ each } className="tp-step-card" style={ {
                  border: '1px solid #eef0f2', borderRadius: 12, padding: 20, marginBottom: 18,
                  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.03)',
                } }>
                  <div className="tp-step-card" style={ { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 } }>
                    <span style={ {
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 28, height: 28, borderRadius: 8,
                      background: '#eef4ff', color: '#1677ff', fontWeight: 700, fontSize: 13, flexShrink: 0,
                    } }>{ each + 1 }</span>
                    <Text strong>Treatment step no. { each + 1 }</Text>
                  </div>
                  <TeethIcon setSelectedAttachments={ setSelectedAttachments } selectedAttachments={ selectedAttachments } stepIndex={ index } iprs={ iprs } setIprs={ setIprs } />
                <TreatmentStepRange
                  value={ treamentStepsData[index] }
                  onChange={ (field, next) => updateStepRange(index, field, next) }
                />
                  <div className="tp-step-card" style={ { textAlign: 'center', paddingTop: 18 } }>
                    <ButtonComponent onClick={ () => {
                      setTreamentSteps((prev) => prev.filter((item) => item !== each));
                      setTreamentStepsData((prev) => prev.filter((_, i) => i !== index));
                    } } type="default" danger ghost>
                      <Icon icon="tabler:trash" style={ { marginRight: 6 } } />Delete Treatment Plan Step
                    </ButtonComponent>
                  </div>
                </div>

              ) }
              <div className="tp-step-card" style={ { width: '100%', textAlign: 'center', paddingTop: 6 } }>
                <ButtonComponent onClick={ () => {
                  setTreamentSteps((prev) => [...prev, prev.length]);
                  // Keep treamentStepsData the same length as treamentSteps —
                  // it previously only grew when the user actually typed into
                  // the From/To inputs, so a step nobody touched those on
                  // would silently vanish from the saved treatment_steps
                  // list (and with it, its teeth diagram) on submit.
                  setTreamentStepsData((prev) => [...prev, { from: '', to: '' }]);
                } } type="primary">
                  <Icon icon="tabler:plus" style={ { marginRight: 6 } } />Add Treatment Plan Step
                </ButtonComponent>
              </div>
            </SectionCard>
          </div>


          <div style={ { display: currentStep === 4 ? 'block' : 'none' } } className="step4">
            <SectionCard icon="tabler:notes" title="Technician's notes" subtitle="Add notes and comments">
              <Editor style={ { height: '200px' } } onChange={ (e) => setTechnicianNote(e) } inialData={ technicianNote } />
            </SectionCard>
          </div>


          <div style={ { display: currentStep === 5 ? 'block' : 'none' } } className="step5">
            <SectionCard icon="tabler:cube-3d-sphere" title="3D Set up" subtitle="Add the link for viewing">
              <Form.Item onChange={
                () => setErrors([])
              } name="3d_Setup_link" rules={ rules.setup3dlink } label="Link :"
                { ...getErrorProps(errors['setup3dlink']) }
              >
                <Input style={ inputHeight } placeholder="LINK" />
              </Form.Item>
            </SectionCard>
          </div>

            <div className="tp-nav">
            <ButtonComponent
              className="tp-nav-back"
              htmlType="button"
              disabled={ currentStep === 0 }
              onClick={ () => currentStep !== 0 && setCurrentStep(currentStep - 1) }
            >
              <span className="tp-btn">
                <Icon icon="tabler:arrow-left" width={ 18 } />
                Back
              </span>
            </ButtonComponent>

            {/*
              Both branches are htmlType="button" and carry their own key.
              As a submit button this was reconciled in place with Next:
              the click that moved you from step 5 to 6 flipped the very
              DOM node being clicked from type=button to type=submit, so
              the browser's default action then submitted the form — the
              plan saved itself on arrival at the 3D setup step, before
              there was a link to save. Submitting is an explicit call now.
            */ }
            { currentStep !== 5
              ? <ButtonComponent key="next" type="primary" htmlType="button"
                onClick={ async () => {
                if (currentStep === 3 && blockedByStepRange()) return;
                const fields = FIELDS_BY_STEP[currentStep];
                if (fields) {
                  try {
                    await form.validateFields(fields);
                  } catch (e) {
                    return;
                  }
                }
                setCurrentStep(currentStep + 1);
              } }>
                <span className="tp-btn">
                  Next
                  <Icon icon="tabler:arrow-right" width={ 18 } />
                </span>
              </ButtonComponent>
              : <ButtonComponent key="save" type="primary" htmlType="button"
                state={ saving } onClick={ () => form.submit() }>
                <span className="tp-btn">
                  <Icon icon="tabler:check" width={ 18 } />
                  Save changes
                </span>
              </ButtonComponent> }
          </div>
        </Form>
      }

    </div>
  </>)
}


const alignerCountCheck = (value, other) => {
  if (Number(value) < 0) {
    return Promise.reject('Cannot be negative');
  }
  if (Number(value) === 0 && !(Number(other) > 0)) {
    return Promise.reject('Upper and lower jaw cannot both be 0');
  }
  return Promise.resolve();
};

const rules = {
  name: [
    { required: true, message: 'Please input your name!', },
    { min: 3, message: 'Minimum name length is 3', },
    { max: 100, message: 'Maximum name length is 100', },
  ],
  setup3dlink: [
    { required: true, message: 'The animation link is required.' },
  ],
  // A single-arch case only has aligners on one jaw, so either count may
  // be 0 - but not both: a plan with no aligners at all is not a plan.
  uperjaw: [
    { required: true, message: 'Please enter the number of aligners.' },
    ({ getFieldValue }) => ({ validator: (_, value) => alignerCountCheck(value, getFieldValue('aligner_lower_jaw')) }),
  ],
  lowerjaw: [
    { required: true, message: 'Please enter the number of aligners.' },
    ({ getFieldValue }) => ({ validator: (_, value) => alignerCountCheck(value, getFieldValue('aligner_upper_jaw')) }),
  ],
  attachmentUpperjaw: [
    { required: true, message: 'Please enter 0 or 1.' },
    { pattern: /^[01]$/, message: 'Only 0 or 1 is allowed.' },
  ],
  attachmentLowerjaw: [
    { required: true, message: 'Please enter 0 or 1.' },
    { pattern: /^[01]$/, message: 'Only 0 or 1 is allowed.' },
  ],
};

export default UpdateTreatmentPlan
